import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ArrowheadRightDarkIcon } from "../../../../assets";
import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import { CursorChangeProps, ResultType, UpcomingMilestonePropsWithKey } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { GetTaskBoardResponse, searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../service/query";
import { ResultData, Status } from "../../../../service/Shared";
import { useTaskPanelWorkflow } from "../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../useAuth";
import { flattenObject, getTaskParentObjectNavigation } from "../../../../utils";
import { getProjectDetailsByUuid, getWorkflowTasksBoardRoute } from "../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridCursorDataLoadEventHandler,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
} from "../../../../widget";
import { IconLinkCellFormatterData } from "../../../../widget/data/DataGrid/models";
import { LandholdingContext } from "../LandholdingContext";

interface UseDashboardWorkflowsAndTasksReturnData {
  upcomingMilestones: UpcomingMilestonePropsWithKey[];
  isLoadingMilestones: boolean;
  hasMilestonesError: boolean;
  isUpcomingTasksLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  upcomingTasksOnChange: DataGridCursorDataLoadEventHandler;

  showUpcomingMilestonesPanel: boolean;
  setShowUpcomingMilestonesPanel: (show: boolean) => void;
  showUpcomingTasksPanel: boolean;
  setShowUpcomingTasksPanel: (show: boolean) => void;

  workflow: GetTaskBoardResponse["results"][number] | undefined;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  showWorkflowTaskPanel: boolean;
  handleCloseWorkflowTaskPanel: () => void;
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void;
  setShowWorkflowTaskPanel: (show: boolean) => void;

  handleRefresh: () => void;
  refreshTasks: boolean;
}

export const useDashboardWorkflowsAndTasks = (): UseDashboardWorkflowsAndTasksReturnData => {
  const location = useLocation();

  const { landholdingDetails } = useContext(LandholdingContext);
  const { currentUserType } = useAuth();
  const navigate = useNavigate();

  const [upcomingMilestones, setUpcomingMilestones] = useState<UpcomingMilestonePropsWithKey[]>([]);
  const [isLoadingMilestones, setIsLoadingMilestones] = useState(true);
  const [hasMilestonesError, setHasMilestonesError] = useState(false);
  /** Identifies the newest milestone fetch, so a slower earlier one cannot overwrite its result. */
  const milestonesRequestId = useRef(0);
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();

  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);
  const [showUpcomingMilestonesPanel, setShowUpcomingMilestonesPanel] = useState(false);
  const [showUpcomingTasksPanel, setShowUpcomingTasksPanel] = useState(false);

  const [isUpcomingTasksLoading, setIsUpcomingTasksLoading] = useState(true);

  const [refreshTasks, setRefreshTasks] = useState(false);

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const workflow = useTaskPanelWorkflow(selectedPanelTask?.workflowUuid);

  // --- For upcoming milestones ---
  const fetchUpcomingMilestonesData = useCallback(
    async (landholdingUuid: string) => {
      // Only the newest fetch may touch milestone state — `refreshTasks` can start another while
      // this one is still in flight.
      milestonesRequestId.current += 1;
      const requestId = milestonesRequestId.current;
      const isCurrentRequest = (): boolean => requestId === milestonesRequestId.current;

      setIsLoadingMilestones(true);
      setHasMilestonesError(false);
      try {
        const result = await searchWorkflowTasks({
          paging: {
            limit: 5,
            afterCursor: null,
            beforeCursor: null,
          },
          filter: {
            results: {
              isMilestone: {
                operator: "eq",
                value: true,
              },
              deadlineDate: {
                operator: "neq",
                value: null,
              },
              status: {
                operator: "neq",
                value: TaskConstants.TASK_STATUS_COMPLETE,
              },
              groups: {
                some: {
                  groupUuid: {
                    operator: "eq",
                    value: landholdingUuid,
                  },
                },
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
          },
          sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
        });
        if (!isCurrentRequest()) {
          return;
        }
        if (result.status !== Status.Success || result.data === undefined) {
          setHasMilestonesError(true);
          return;
        }
        setUpcomingMilestones(
          result.data.results.map((task) => ({
            key: task.uuid,
            name: task.name,
            deadlineDate: task.deadlineDate ? task.deadlineDate : undefined,
            status: task.status,
            assigneeName: task.assignee?.name ?? undefined,
            // The uniform parent, so the name and its link always refer to the same object — and
            // general milestone tasks, which have no `workflowObject*`, get one too.
            project: task.parentObjectName ?? undefined,
            onProjectClick: getTaskParentObjectNavigation(
              task.parentObjectType,
              task.parentObjectUuid,
              currentUserType,
              navigate,
              { goBackText: "Back to landholding", from: location.pathname }
            ),
            onClick: () => {
              setSelectedPanelTask(task);
              setShowWorkflowTaskPanel(true);
            },
          }))
        );
      } catch {
        // A rejected search never reached the status check, which used to leave the card on its
        // skeletons for good.
        if (isCurrentRequest()) {
          setHasMilestonesError(true);
        }
      } finally {
        if (isCurrentRequest()) {
          setIsLoadingMilestones(false);
        }
      }
    },
    [landholdingDetails?.groupGuid, currentUserType, navigate, location.pathname]
  );

  // --- For upcoming tasks data grid ---
  const formatUpcomingTasksData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };
        result.avatar = { fullName: d.assignee?.name };
        result.taskLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        // The project the task belongs to is the workflow's object (workflowObjectUuid/Name), not the task's own
        // objectUuid (which is a task-level reference that is null for most tasks — leaving the cell blank).
        if (d.workflowObjectUuid) {
          result.projectLink = {
            text: d.workflowObjectName,
            to: getProjectDetailsByUuid(d.workflowObjectUuid, currentUserType),
            state: { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin },
          } as DataGridLinkCellFormatterData;
        }

        // A general task has no workflow, so there is no tasks board to link to.
        if (d.workflowUuid) {
          result.viewTasksBoardIcon = {
            to: getWorkflowTasksBoardRoute(d.workflowUuid, currentUserType),
            state: { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin },
            icon: <ArrowheadRightDarkIcon />,
          } as IconLinkCellFormatterData;
        }

        return result;
      }) || []
    );
  }, []);

  const upcomingTasksOnChange: DataGridCursorDataLoadEventHandler = async ({
    paging,
    filtering,
    sorting,
  }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 6,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    if (!landholdingDetails) return data;

    setIsUpcomingTasksLoading(true);

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    filterCriteria.groups = {
      some: {
        groupUuid: {
          operator: "eq",
          value: landholdingDetails?.groupGuid,
        },
      },
    };

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }
    sorting.forEach((s) => {
      switch (s.key) {
        case "avatar":
          // eslint-disable-next-line no-param-reassign
          s.key = "assignee.name";
          break;
        default:
          break;
      }
    });

    await searchWorkflowTasks({
      paging: {
        limit: 6,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatUpcomingTasksData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || response.data?.results?.length || 0,
          },
        };
      })
      .finally(() => {
        setIsUpcomingTasksLoading(false);
      });
    return data;
  };

  useEffect(() => {
    if (landholdingDetails?.groupGuid) {
      fetchUpcomingMilestonesData(landholdingDetails?.groupGuid);
    }
  }, [landholdingDetails?.groupGuid, refreshTasks]);

  return {
    isUpcomingTasksLoading,
    upcomingTasksOnChange,

    isLoadingMilestones,
    hasMilestonesError,

    showUpcomingMilestonesPanel,
    setShowUpcomingMilestonesPanel,
    showUpcomingTasksPanel,
    setShowUpcomingTasksPanel,
    refreshTasks,
    handleRefresh,

    workflow,

    upcomingMilestones,

    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    setSelectedPanelTask,
    setShowWorkflowTaskPanel,
  };
};
